// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Modal, Dropdown as BSDrowdown } from "react-bootstrap";
import { Form, Row } from "react-bootstrap";
import InputNumber from "rc-input-number";

import { DataState } from "types";
import { isSet } from "shared/utils/utils";
import { ActionType, IAction, raiseAction } from "actions";
import { IActionChangeChargerSettings, IActionTypeConfirmPopup } from "actions";
import { IState } from "states/states";
import { ModalPanel } from "states/guistates";
import { IConnectorConfig, IOcppCharger } from "states/ocppstates";

import SaveFeedback from "view/comp/SaveFeedback";
import CustomDropdown from "view/comp/Dropdown";
import TariffAssociations from "view/comp/TariffAssociations";

type IModalChargerSettingsItemProps = {
  isOpen: boolean;
  dataState: DataState;
  charger: IOcppCharger | undefined;
  connectorConfig: (IConnectorConfig | undefined)[] | undefined;
  canModifyOcpp: boolean;
  canAdminChargers: boolean;
};

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curUser) => curUser.secId === state.site.user?.secId);
  const chargerIndex = state.ocpp.charger.findIndex(
    (curItem) => curItem.fk_charger === state.gui.modalCharger,
  );
  const props: IModalChargerSettingsItemProps = {
    isOpen: state.gui.modal.panel === ModalPanel.CHARGER_SETTINGS,
    dataState: state.gui.modal.dataState,
    charger: chargerIndex >= 0 ? state.ocpp.charger[chargerIndex] : undefined,
    connectorConfig: chargerIndex >= 0 ? state.ocpp.connectorConfig[chargerIndex] : undefined,
    canModifyOcpp: curUser?.accessRights.ocppSettings ?? false,
    canAdminChargers: curUser?.accessRights.ocppChargerAdmin ?? false,
  };
  return props;
};

interface IModalChargerSettingsState {
  wasOpen: boolean;
  isChanged: boolean;
  name: string | undefined;
  restrictAuth: boolean;
  longitude: number;
  latitude: number;
  connector: (IConnectorConfig | undefined)[];
}

const EMPTY_CONNECTOR_CONFIG: IConnectorConfig = {
  name: undefined,
  evseid: undefined,
  plugType: undefined,
  nominalPower: undefined,
  isDc: undefined,
  nrPhases: undefined,
};

interface IModalChargerSettingsItemDispatch {
  onSaveClick: (id: number, state: IModalChargerSettingsState) => void;
  onConfirmDelete: (id: number, title: string, message: string) => void;
  onCancelClick: () => void;
  onShowErrorPopup: (title: string, message: string) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IModalChargerSettingsItemDispatch = {
    onSaveClick: (id: number, state: IModalChargerSettingsState) => {
      const payload: IActionChangeChargerSettings = {
        id,
        name: state.name,
        restrictAuth: state.restrictAuth,
        connector: state.connector,
        coordinates: [state.latitude, state.longitude],
      };
      dispatch(raiseAction(ActionType.ACT_SAVE_OCPP_CHARGERS_REQ, payload));
    },
    onConfirmDelete: (id: number, title: string, message: string) => {
      const confirmPayload: IActionChangeChargerSettings = {
        id,
        deleteCharger: true,
      };
      const payload: IActionTypeConfirmPopup = {
        title,
        message,
        action: ActionType.ACT_SAVE_OCPP_CHARGERS_REQ,
        actionPayload: confirmPayload,
      };
      dispatch(raiseAction(ActionType.ACT_CONFIRM_POPUP, payload));
    },
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
    },
    onShowErrorPopup: (title: string, message: string) => {
      const payload: IActionTypeConfirmPopup = {
        title,
        message,
      };
      dispatch(raiseAction(ActionType.ACT_CONFIRM_POPUP, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  restrictAuthCaption: {
    id: "select charging authentication",
  },
  confirmDeleteTitle: {
    id: "Delete charger",
  },
  confirmDeleteBody: {
    id: "This will delete this charger. All past charging sessions and charging data will be lost. Are you sure?",
  },
  invalidCoordinatesTitle: {
    id: "Invalid Coordinates",
  },
  invalidCoordinatesRangeMessage: {
    id: "Invalid coordinate values. Latitude must be between -90 and 90, longitude between -180 and 180.",
  },
  invalidCoordinatesFormatMessage: {
    id: "Invalid coordinate format. Please use format: latitude, longitude (e.g., 48.203951, 16.402388)",
  },
  clipboardErrorTitle: {
    id: "Clipboard Error",
  },
  clipboardErrorMessage: {
    id: "Failed to read from clipboard. Please ensure your browser supports clipboard access.",
  },
});

class ModalChargerSettingsItemComponent extends React.Component<
  IModalChargerSettingsItemProps & IModalChargerSettingsItemDispatch,
  IModalChargerSettingsState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public state: IModalChargerSettingsState = {
    wasOpen: false,
    isChanged: false,
    name: undefined,
    restrictAuth: false,
    connector: [],
    longitude: 0,
    latitude: 0,
  };

  componentDidUpdate() {
    // check for changes in old value
    if (this.props.isOpen !== this.state.wasOpen) {
      this.setState({
        wasOpen: this.props.isOpen,
        isChanged: false,
        name: this.props.charger?.name,
        restrictAuth: this.props.charger?.restrictAuth ?? false,
        connector: this.props.connectorConfig ?? [],
        latitude: this.props.charger?.coordinates?.[0] ?? 48.203951,
        longitude: this.props.charger?.coordinates?.[1] ?? 16.402388,
      });
    }
  }

  public render() {
    if (!this.props.isOpen || this.props.charger === undefined) return <></>; // avoid rendering of inexisting data

    return (
      <Modal show={this.props.isOpen} onHide={this.props.onCancelClick} id="modal-charger-settings">
        <Modal.Header closeButton>
          <FormattedMessage id="Charger Settings" />
        </Modal.Header>
        <Modal.Body style={{ overflow: "visible" }}>
          {this.renderSettings()}
          {this.renderConnectorTable()}
          {this.props.canAdminChargers && (
            <Button
              id="deleteCharger"
              variant="danger"
              className="mt-2"
              onClick={() =>
                this.props.onConfirmDelete(
                  this.props.charger!.fk_charger,
                  this.context.formatMessage(messages.confirmDeleteTitle),
                  this.context.formatMessage(messages.confirmDeleteBody),
                )
              }
            >
              <FormattedMessage id="Delete Charger" />
            </Button>
          )}
        </Modal.Body>
        <Modal.Footer>
          <SaveFeedback dataState={this.props.dataState} />
          <Button
            id="cancelChargerSettings"
            variant="secondary"
            className="me-1"
            onClick={this.props.onCancelClick}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          <Button
            id="saveChargerSettings"
            variant="primary"
            onClick={() => this.props.onSaveClick(this.props.charger!.fk_charger, this.state)}
            disabled={this.props.dataState === DataState.SAVE_REQ || !this.state.isChanged}
          >
            <FormattedMessage id="Save" />
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  private renderSettings() {
    const chargerId = this.props.charger!.fk_charger;
    const curSettings =
      this.state.restrictAuth ?
        <FormattedMessage id="restricted charging" />
      : <FormattedMessage id="charging always allowed" />;

    return (
      <>
        <table>
          <tbody>
            <tr>
              <td>
                <FormattedMessage id="Charger name" />:
              </td>
              <td colSpan={2}>
                {this.props.canModifyOcpp ?
                  <Form.Control
                    type="text"
                    value={this.state.name ?? ""}
                    onChange={(event) =>
                      this.setState({
                        isChanged: true,
                        name: event.target.value ? event.target.value : undefined,
                      })
                    }
                  />
                : this.state.name}
              </td>
            </tr>
            <tr>
              <td>
                <FormattedMessage id="Charging authentication" />:
              </td>
              <td colSpan={2}>
                {this.props.canModifyOcpp ?
                  <CustomDropdown
                    items={[
                      <BSDrowdown.Item
                        key={0}
                        onClick={() => this.setState({ isChanged: true, restrictAuth: false })}
                      >
                        <FormattedMessage id="charging always allowed" />
                      </BSDrowdown.Item>,
                      <BSDrowdown.Item
                        key={1}
                        onClick={() => this.setState({ isChanged: true, restrictAuth: true })}
                      >
                        <FormattedMessage id="restricted charging" />
                      </BSDrowdown.Item>,
                    ]}
                    curItem={curSettings}
                  />
                : curSettings}
              </td>
            </tr>
            <tr>
              <td rowSpan={3} style={{ verticalAlign: "top", paddingTop: 6 }}>
                <FormattedMessage id="Location" />:
              </td>
              <td>
                <FormattedMessage id="Latitude" />
              </td>
              <td>
                {this.props.canModifyOcpp ?
                  <InputNumber
                    type="number"
                    className="noArrows fullwidth default-border w120"
                    min={-180}
                    max={180}
                    value={this.state.latitude}
                    onChange={(number) => {
                      if (isSet(number)) {
                        this.setState({
                          isChanged: true,
                          latitude: number,
                        });
                      }
                    }}
                  />
                : this.state.latitude}
              </td>
            </tr>
            <tr>
              <td>
                <FormattedMessage id="Longitude" />
              </td>
              <td>
                {this.props.canModifyOcpp ?
                  <InputNumber
                    type="number"
                    className="noArrows fullwidth default-border w120"
                    min={-90}
                    max={90}
                    value={this.state.longitude}
                    onChange={(number) => {
                      if (isSet(number)) {
                        this.setState({
                          isChanged: true,
                          longitude: number,
                        });
                      }
                    }}
                  />
                : this.state.longitude}
              </td>
            </tr>
            <tr>
              <td colSpan={2}>
                {this.props.canModifyOcpp && (
                  <Button variant="outline-success" size="sm" onClick={this.onPasteCoordinates}>
                    <FormattedMessage id="Paste from Clipboard" />
                  </Button>
                )}
              </td>
            </tr>
            {this.props.canModifyOcpp && isSet(chargerId) && (
              <tr className="mt-1">
                <td style={{ verticalAlign: "top" }}>
                  <FormattedMessage id="Cost Tariffs" />:
                </td>
                <td colSpan={2}>
                  <TariffAssociations chargerId={chargerId} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <p></p>
      </>
    );
  }

  private renderConnectorTable() {
    const nrConnectors =
      this.props.charger?.numberofconnectors ?? (this.props.connectorConfig?.length ?? 2) - 1;
    if (nrConnectors < 1) {
      return <></>;
    }
    const rows = Array.from(Array(nrConnectors).keys()).map((curIndex) =>
      this.renderConnector(curIndex),
    );
    return (
      <>
        <h2>
          <FormattedMessage id="Connectors" />
        </h2>
        <table>
          <thead>
            <tr>
              <th></th>
              <th>
                <FormattedMessage id="Name" />
              </th>
              <th>
                <FormattedMessage id="EVSE-ID" />
              </th>
              <th className="optional-md">
                <FormattedMessage id="Plug" />
              </th>
              <th className="optional-md">
                <FormattedMessage id="Power" />
              </th>
              <th className="optional-md">
                <FormattedMessage id="Phases" />
              </th>
              <th className="optional-md">
                <FormattedMessage id="DC" />
              </th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </>
    );
  }

  private renderConnector(index: number) {
    const curSettings = this.state.connector[index + 1] ?? EMPTY_CONNECTOR_CONFIG;
    return (
      <tr key={index}>
        <td>{index + 1}:</td>
        <td>
          {this.props.canAdminChargers ?
            <Form.Control
              type="text"
              value={curSettings.name ?? ""}
              onChange={(event) => this.onChangeConnectorProp(index, "name", event.target.value)}
            />
          : curSettings.name}
        </td>
        <td>
          {this.props.canAdminChargers ?
            <Form.Control
              type="text"
              value={curSettings.evseid ?? ""}
              onChange={(event) => this.onChangeConnectorProp(index, "evseid", event.target.value)}
            />
          : curSettings.evseid}
        </td>
        <td className="optional-md">
          {this.props.canAdminChargers ?
            <Form.Control
              type="text"
              value={curSettings.plugType ?? ""}
              onChange={(event) =>
                this.onChangeConnectorProp(index, "plugType", event.target.value)
              }
            />
          : curSettings.plugType}
        </td>
        <td className="optional-md">
          {this.props.canAdminChargers ?
            <InputNumber
              value={curSettings.nominalPower}
              min={0}
              max={1000}
              size={1}
              onChange={(value) => this.onChangeConnectorProp(index, "nominalPower", value)}
            />
          : curSettings.nominalPower}
        </td>
        <td className="optional-md">
          {this.props.canAdminChargers ?
            <InputNumber
              value={curSettings.nrPhases}
              min={1}
              max={3}
              size={1}
              onChange={(value) => this.onChangeConnectorProp(index, "nrPhases", value)}
            />
          : curSettings.nrPhases}
        </td>
        <td className="optional-md">
          <Form.Check
            disabled={!this.props.canAdminChargers}
            checked={curSettings.isDc ?? false}
            onChange={(event) => this.onChangeConnectorProp(index, "isDc", event.target.checked)}
          />
        </td>
      </tr>
    );
  }

  private onChangeConnectorProp(
    index: number,
    prop: string,
    value: string | number | boolean | null,
  ) {
    if (isSet(value)) {
      const curSettings = this.state.connector[index + 1] ?? EMPTY_CONNECTOR_CONFIG;
      const connector = [...this.state.connector];
      connector[index + 1] = {
        ...curSettings,
        [prop]: value,
      };
      this.setState({
        isChanged: true,
        connector,
      });
    }
  }

  private onPasteCoordinates = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      const coordinateRegex = /^([-+]?[0-9]+[,.]?[0-9]*)[, ]+([-+]?[0-9]+[,.]?[0-9]*)$/;
      const match = clipboardText.trim().match(coordinateRegex);

      if (match) {
        // Parse the coordinates and normalize decimal separator
        const latStr = match[1].replace(",", ".");
        const lngStr = match[2].replace(",", ".");

        const latitude = parseFloat(latStr);
        const longitude = parseFloat(lngStr);

        // Validate coordinate ranges
        if (latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180) {
          this.setState({
            isChanged: true,
            latitude,
            longitude,
          });
        } else {
          this.props.onShowErrorPopup(
            this.context.formatMessage(messages.invalidCoordinatesTitle),
            this.context.formatMessage(messages.invalidCoordinatesRangeMessage),
          );
        }
      } else {
        this.props.onShowErrorPopup(
          this.context.formatMessage(messages.invalidCoordinatesTitle),
          this.context.formatMessage(messages.invalidCoordinatesFormatMessage),
        );
      }
    } catch (error) {
      this.props.onShowErrorPopup(
        this.context.formatMessage(messages.clipboardErrorTitle),
        this.context.formatMessage(messages.clipboardErrorMessage),
      );
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ModalChargerSettingsItemComponent);
